import { XMarkIcon } from "@heroicons/react/20/solid";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { fetchContentPage, peekContentPage } from "../services/contentService";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The language the portal is in, as the API's `Accept-Language` expects it. */
const apiLanguage = (language) => (String(language || "en").startsWith("ar") ? "ar" : "en");

/**
 * A public content page (Terms of Service, Privacy Policy) read in place.
 *
 * Opened from the sign-up form's "I agree" label, so reading the terms never
 * leaves the half-filled form. "I agree" ticks the box and closes; Close, Esc
 * and a click on the backdrop just close.
 *
 * The body is the server-sanitised HTML from `GET /pages/{slug}` (see
 * contentService), rendered with the `.content-prose` typography. Its
 * direction follows the language the API answered in, not the UI's: an
 * Arabic page reads right-to-left even if only its English fallback exists.
 *
 * Unpublished (404) says so plainly. Nothing here blocks sign-up — the
 * checkbox works whether or not the page can be read.
 */
export default function ContentPageModal({ slug, fallbackTitle, onClose, onAgree }) {
  const { t, i18n } = useTranslation();
  const language = apiLanguage(i18n.language);

  const dialogRef = useRef(null);
  const bodyRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const initialState = () => {
    const cached = peekContentPage(slug, language);
    if (cached === undefined) return { status: "loading" };
    return cached ? { status: "ready", page: cached } : { status: "missing" };
  };
  const [state, setState] = useState(initialState);

  const load = useCallback(() => {
    const cached = peekContentPage(slug, language);
    if (cached !== undefined) {
      setState(cached ? { status: "ready", page: cached } : { status: "missing" });
      return () => {};
    }

    let live = true;
    setState({ status: "loading" });
    fetchContentPage(slug, language)
      .then((page) => live && setState(page ? { status: "ready", page } : { status: "missing" }))
      .catch(() => live && setState({ status: "error" }));

    return () => {
      live = false;
    };
  }, [slug, language]);

  useEffect(() => load(), [load]);

  // Esc, a focus trap, no page scroll behind, and focus handed back on close.
  useEffect(() => {
    const previous = document.activeElement;
    closeRef.current?.focus();
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;

      const nodes = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
      if (!nodes.length) {
        e.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const inside = dialogRef.current.contains(document.activeElement);

      if (e.shiftKey && (!inside || document.activeElement === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || document.activeElement === last)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, []);

  // Links in the page open in a new tab, so the form stays where it is.
  useEffect(() => {
    if (state.status !== "ready" || !bodyRef.current) return;
    bodyRef.current.querySelectorAll("a[href]").forEach((a) => {
      if (/^(mailto|tel):/i.test(a.getAttribute("href"))) return;
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });
  }, [state]);

  const page = state.status === "ready" ? state.page : null;
  const pageDir = page?.locale === "ar" ? "rtl" : page ? "ltr" : undefined;
  const titleId = `content-page-title-${slug}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={state.status === "loading"}
        className="flex h-full w-full flex-col bg-surface shadow-xl sm:h-auto sm:max-h-[80vh] sm:max-w-2xl sm:rounded-xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-ink" lang={page?.locale} dir={pageDir}>
            {page?.title || fallbackTitle}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="-m-1 rounded p-1 text-ink-2 hover:bg-raised hover:text-ink"
            aria-label={t("signup.legal.close")}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {state.status === "loading" && (
            <div className="space-y-3" aria-label={t("signup.legal.loading")}>
              <div className="h-5 w-1/2 animate-pulse rounded bg-raised" />
              <div className="h-4 w-full animate-pulse rounded bg-raised" />
              <div className="h-4 w-11/12 animate-pulse rounded bg-raised" />
              <div className="h-4 w-4/5 animate-pulse rounded bg-raised" />
              <div className="h-4 w-full animate-pulse rounded bg-raised" />
              <div className="h-4 w-3/5 animate-pulse rounded bg-raised" />
            </div>
          )}

          {state.status === "error" && (
            <div className="py-6 text-center" role="alert">
              <p className="text-sm text-ink">{t("signup.legal.error")}</p>
              <button type="button" className="btn-secondary mt-4" onClick={load}>
                {t("signup.legal.retry")}
              </button>
            </div>
          )}

          {state.status === "missing" && (
            <div className="py-6 text-center" role="status">
              <p className="text-sm font-semibold text-ink">{t("signup.legal.unavailableTitle")}</p>
              <p className="mt-1 text-sm text-ink-2">{t("signup.legal.unavailable")}</p>
            </div>
          )}

          {page && (
            <div
              ref={bodyRef}
              className="content-prose"
              lang={page.locale}
              dir={pageDir}
              // Sanitised server-side on save; see contentService.
              dangerouslySetInnerHTML={{ __html: page.body ?? "" }}
            />
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-line px-5 py-3 sm:flex-row sm:justify-end">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t("signup.legal.close")}
          </button>
          {page && (
            <button type="button" className="btn-primary" onClick={onAgree}>
              {t("signup.legal.agree")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
